/* eslint-disable */
// useTypeEmailPolicy — what one notification type's email setting is, for the
// composer's per-send checkbox.
//
// The composer needs three facts about the type it is about to send as, and no
// existing hook carries them: useAnnouncementAudience answers "who", and
// useNotificationPreferences answers "what reaches ME" — filtered to types the
// reader can receive, which is the wrong set for a sender. So this is its own
// small read rather than a widening of either.
//
// ⚠️ IT IS A DEFAULT, NOT A GATE. The checkbox this feeds is a convenience: the
// real decision is made twice on the server, once by the enqueue trigger and
// again by notification_email_claim ten minutes later, and both re-read the type
// and the person. If this query fails, the composer must still be able to send —
// so `loading` and a null policy mean "leave it to the type", which is exactly
// what omitting `email` from the payload does.
//
// ⚠️ silent AND critical ARE NOT OPINIONS. nt_silent_never_mails and
// nt_critical_always_mails are CHECK constraints on notification_types, so the
// database refuses the other value. The checkbox renders disabled on those two
// levels with the reason beside it.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';

export const TYPE_EMAIL_QUERY_KEY = ['system', 'notifications', 'type-email'];

export function useTypeEmailPolicy(typeKey) {
  const q = useQuery({
    queryKey: [...TYPE_EMAIL_QUERY_KEY, typeKey || 'none'],
    enabled: !!typeKey,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_types')
        .select('key, default_severity, email_enabled')
        .eq('key', typeKey)
        .maybeSingle();
      if (error) throw error;
      return data || null;
    },
  });

  const row = q.data || null;
  const severity = row?.default_severity || null;

  return {
    loading: q.isLoading,
    // null until known — the caller must not read it as "off".
    emailEnabled: row ? row.email_enabled === true : null,
    alwaysMails: severity === 'critical',
    neverMails: severity === 'silent',
    reason: severity === 'critical'
      ? 'Critical announcements are always emailed.'
      : severity === 'silent'
        ? "This type doesn't send email."
        : null,
  };
}

export default useTypeEmailPolicy;
